/**
 * Static intake questions — deterministic, no AI call. (Port of lib/intake_questions.py.)
 *
 * The first questions of a rectification session used to be an OpenAI call, but they are
 * the same high-signal prompts for every user: the astrologically useful event categories
 * are fixed (relocation, marriage, children, career, family death, health...), and the
 * question wording never needed per-user generation — only light age-gating. Producing
 * them in code makes session start instant, free, and offline-safe.
 *
 * The dict shape matches what the AI used to return ({id, question, suggestions,
 * answer_kind}) so the UI and the fact-extraction call are unchanged.
 */

import { diffDays, todayLocal, tryParseISODate } from "./datetime";
import type { Profile } from "../types";

// Below this age, prompts that assume marriage, children, or a long career are dropped
// (mirrors the old instruction "if the user is young, do not ask questions that assume
// marriage, children, or a long career").
export const ADULT_MILESTONE_MIN_AGE = 16;

const _DATE_REMINDER =
  "Exact dates matter most — give the date (or closest month/year), the city/place, " +
  "and the approximate time of day if remembered. Even an approximate month is far " +
  "better than no date.";

/** Whole-year age from the profile's birth date, or None if unparseable. */
export function age_years(user_profile: Profile): number | null {
  const birth_date = tryParseISODate((user_profile["birth"] || {})["date"] || "");
  if (birth_date === null) return null;
  return Math.max(0, Math.trunc(diffDays(todayLocal(), birth_date) / 365.2425));
}

/**
 * The first-pass intake prompts, age-gated. All prompts are answered in one
 * combined free-text box; the strongest first-pass evidence (relocation, marriage,
 * children) leads when age-appropriate.
 */
export function initial_intake_questions(user_profile: Profile): Record<string, any>[] {
  const age = age_years(user_profile);
  const is_adult = age === null || age >= ADULT_MILESTONE_MIN_AGE;

  const big_milestones = ["Moves to a new city or country (relocation is one of the strongest signals)"];
  if (is_adult) {
    big_milestones.push(
      "Marriage, engagement, or a serious relationship becoming formal — or a separation/divorce",
      "Children: birth, pregnancy loss, adoption, fertility treatment, or another major child-related event",
    );
  }
  const questions: Record<string, any>[] = [
    {
      id: "major_dated_milestones",
      question: `What were the biggest turning points of your life, with dates? ${_DATE_REMINDER}`,
      suggestions: big_milestones,
      answer_kind: "event",
    },
    {
      id: "career_education_events",
      question: `Tell us about your education and work history as dated events. ${_DATE_REMINDER}`,
      suggestions: (is_adult
        ? [
            "First job, a major promotion, a career break or failure, changing fields, starting a business",
            "Public recognition, an award, or a notable achievement",
          ]
        : []
      ).concat(["Starting or finishing school/college, exam milestones, going abroad to study"]),
      answer_kind: "event",
    },
    {
      id: "family_health_events",
      question: `Any significant family or health events, with dates? ${_DATE_REMINDER}`,
      suggestions: [
        "Death of a parent, grandparent, or close family member",
        "A serious injury, surgery, hospitalization, or health crisis",
        "Buying/selling a home or another major property event",
      ],
      answer_kind: "event",
    },
    {
      id: "stable_traits",
      question:
        "Describe the person as they ARE (no dates needed here): body and face, " +
        "temperament, and anything physically distinctive.",
      suggestions: [
        "Build, height relative to family, complexion relative to family, face shape",
        "Temperament: quick/slow, intense/easygoing, talkative/reserved",
        "Scars, birthmarks, or other distinctive physical features",
        "Profession style and how they approach work (as a stable trait, not dated jobs)",
        // Birth lore (06_birth_circumstances / 12_gandanta_births) — captured as trait questions
        // (birth_place_kind / delivery_ease / father_absent_birth / birth_remedy_lore) and scored
        // deterministically per candidate chart; asked of everyone, never age-gated.
        "Birth lore, if known: where the birth happened (home / travelling / hospital), whether the " +
          "delivery was easy or difficult, whether the father was present, and any special ritual " +
          "performed soon after the birth",
      ],
      answer_kind: "trait",
    },
  ];
  return questions;
}
